import React from 'react';
import styled, { css } from 'styled-components';

import { Row, Col, LazyImage } from '@crehana/ui';

import Slider from 'Jsx/Shared/Sliders/Slider';
import Media from 'Jsx/Shared/ui/Media';

import { useCrehanaLiveTranslations } from 'Jsx/CrehanaLive/hooks';

import { LiveCategories, LiveCategory } from '../../types';

const CategoryCardWrapper = styled.button`
  min-height: 74px;
`;

interface CategoryCardProps extends LiveCategory {
  onClick(): void;
  isSelected?: boolean;
}

const CategoryCard: React.FC<CategoryCardProps> = ({
  id,
  coverImage,
  // eslint-disable-next-line camelcase
  cover_image,
  name,
  onClick,
  isSelected,
}) => (
  <CategoryCardWrapper
    type="button"
    className="w-full flex flex-col sm:flex-row rounded-5 overflow-hidden outline-none"
    onClick={onClick}
    key={id}
    style={{
      transition: 'box-shadow 0.2s ease-in-out',
      boxShadow: isSelected ? '0px 8px 24px rgba(24, 27, 50, 0.48)' : '',
    }}
  >
    <div
      css={css`
        width: 184px;
        height: 172px;
        ${props => props.theme.breakpoints.up('sm')} {
          width: 74px;
          height: 74px;
        }
      `}
      className="bg-primary-light flex-shrink-0"
    >
      <LazyImage
        className="w-full h-full"
        imgClassName="object-cover"
        // eslint-disable-next-line camelcase
        src={cover_image || coverImage}
        alt={name}
        isTransparent
      />
    </div>
    <div
      css={css`
        transition: background-color 0.2s ease-in-out;
        width: 184px;
        height: 64px;
        ${props => props.theme.breakpoints.up('sm')} {
          width: calc(100% - 74px);
          height: 74px;
        }
      `}
      className={`flex flex-shrink-0 py-16 px-40 font-subtitle3 text-center justify-center items-center h-full  ${
        isSelected
          ? 'bg-white text-primary-main'
          : 'text-white bg-base-lighter-24 hover:bg-base-lighter-8'
      }`}
    >
      {name}
    </div>
  </CategoryCardWrapper>
);

interface CategoriesProps {
  liveCategories: LiveCategories;
  categorySelected?: LiveCategory;
  setCategorySelected: (category?: LiveCategory) => void;
}

const Categories: React.FC<CategoriesProps> = ({
  liveCategories,
  setCategorySelected,
  categorySelected,
}) => {
  const { t } = useCrehanaLiveTranslations();

  return (
    <React.Fragment>
      <h2 className="font-h4 text-white text-center">
        {t('EXPLORE_CATEGORYS')}
      </h2>
      <Media from="xs" until="sm">
        <Row className="mt-24">
          <Col xs={12}>
            {/* @ts-ignore (deprecated component) */}
            <Slider
              itemWidth="184px"
              itemHeight="236px"
              itemGap={8}
              showPagination
              dark
            >
              {liveCategories.map(category => (
                <CategoryCard
                  key={category.id}
                  {...category}
                  onClick={() => setCategorySelected(category)}
                />
              ))}
            </Slider>
          </Col>
        </Row>
      </Media>
      <Media from="sm">
        <Row className="mt-8">
          {liveCategories.map(category => {
            const isSelected =
              categorySelected && categorySelected.slug === category.slug;

            return (
              <Col key={category.id} xs={4} lg={3}>
                <CategoryCard
                  {...category}
                  onClick={() => {
                    if (isSelected) {
                      setCategorySelected(undefined);
                    } else {
                      setCategorySelected(category);
                    }
                  }}
                  isSelected={isSelected}
                />
              </Col>
            );
          })}
        </Row>
      </Media>
    </React.Fragment>
  );
};

export default Categories;
